import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { ClickAwayListener } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { blendColors } from "assets/jss/material-dashboard-pro-react";

import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import GridItem from "components/Grid/GridItem";
import DimFilterDialog from "./DimFilterDialog";
import FilterDialog from "components/FilterDialog/FilterDialog";
import FilterBarExtras from "./FilterBarExtras";

// ─── slim scrollbar (hover-reveal, 4px) + panel enter animation ─────────────
const SLIM_SCROLL_CSS = `
  .rfb-slim-scroll {
    scrollbar-width: thin;
    scrollbar-color: transparent transparent;
  }
  .rfb-slim-scroll:hover { scrollbar-color: #bbb transparent; }
  .rfb-slim-scroll::-webkit-scrollbar { width: 4px; }
  .rfb-slim-scroll::-webkit-scrollbar-track { background: transparent; }
  .rfb-slim-scroll::-webkit-scrollbar-thumb { background: transparent; border-radius: 2px; }
  .rfb-slim-scroll:hover::-webkit-scrollbar-thumb { background: #ccc; }

  @keyframes rfb-grow-in {
    from { opacity: 0; transform: scale(0.8); }
    to   { opacity: 1; transform: scale(1); }
  }
  .rfb-panel-enter {
    animation: rfb-grow-in 200ms cubic-bezier(0.4, 0, 0.2, 1) forwards;
    transform-origin: top left;
  }
`;

// ─── DimDropdown ─────────────────────────────────────────────────────────────
// sections: [{ key, label, filterKeys[] }] — opcional.  Quando vazio, lista flat.
function DimDropdown({
  filtersList,
  filtersNames,
  commonFilters,
  commonFiltersSequence,
  sections,
  language,
  onSelect,
  color,
  customTrigger,
  onOpenChange,
  includePeriodInSequence,
  showActiveIndicators = true,
}) {
  const [open, setOpen] = useState(false);
  const onOpenChangeRef = React.useRef(onOpenChange);
  React.useLayoutEffect(() => {
    onOpenChangeRef.current = onOpenChange;
  });
  const handleSetOpen = React.useCallback((valOrFn) => {
    setOpen((prev) => {
      const next = typeof valOrFn === "function" ? valOrFn(prev) : valOrFn;
      if (onOpenChangeRef.current) onOpenChangeRef.current(next);
      return next;
    });
  }, []);
  const [panelStyle, setPanelStyle] = useState(null);
  const containerRef = React.useRef(null);
  const panelRef = React.useRef(null);
  const { t } = useTranslation();

  const calcPanelStyle = React.useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();

    // Detect offset caused by ancestor with CSS transform
    // (transform creates a new containing block for position:fixed)
    const probe = document.createElement("div");
    probe.style.cssText =
      "position:fixed;top:0;left:0;width:0;height:0;pointer-events:none";
    containerRef.current.appendChild(probe);
    const probeRect = probe.getBoundingClientRect();
    containerRef.current.removeChild(probe);
    const fixedOffX = probeRect.left;
    const fixedOffY = probeRect.top;

    const maxH = Math.max(80, window.innerHeight - rect.bottom - 12);
    const panelW = 200;
    const margin = 8;
    let left = rect.left - fixedOffX;
    if (rect.left + panelW > window.innerWidth - margin) {
      left = Math.max(margin, window.innerWidth - panelW - margin) - fixedOffX;
    }
    setPanelStyle({
      position: "fixed",
      top: rect.bottom + 2 - fixedOffY,
      left,
      maxHeight: maxH,
    });
  }, []);

  // Calcula posição antes do primeiro paint (useLayoutEffect = síncrono)
  React.useLayoutEffect(() => {
    if (!open) {
      setPanelStyle(null);
      return;
    }
    calcPanelStyle();
  }, [open, calcPanelStyle]);

  // Recalcula posição ao scrollar ou redimensionar a janela
  React.useEffect(() => {
    if (!open) return;
    window.addEventListener("scroll", calcPanelStyle, true);
    window.addEventListener("resize", calcPanelStyle);
    return () => {
      window.removeEventListener("scroll", calcPanelStyle, true);
      window.removeEventListener("resize", calcPanelStyle);
    };
  }, [open, calcPanelStyle]);

  React.useEffect(() => {
    const el = panelRef.current;
    if (!el || !open) return;
    const handler = (e) => {
      e.preventDefault();
      el.scrollTop += e.deltaY;
    };
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, [open, panelStyle]);

  // Ordem de ativação usada para numerar o indicador no dropdown
  const activeSeq = commonFiltersSequence.filter(
    (f) =>
      (includePeriodInSequence || f !== "period") &&
      (commonFilters[f] || []).length > 0,
  );

  const renderDimRow = (f) => {
    const seqIdx = activeSeq.indexOf(f);
    const isActive = seqIdx !== -1;
    const seqNum = isActive ? seqIdx + 1 : null;
    const label = filtersNames[f]?.[language] || f;
    const itemColor = isActive && showActiveIndicators ? "#333" : "#555";
    return (
      <div
        key={f}
        onClick={() => {
          handleSetOpen(false);
          onSelect(f);
        }}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "5px 12px 5px 8px",
          cursor: "pointer",
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
          fontWeight: 400,
          fontSize: 13,
          lineHeight: "1.5em",
          color: itemColor,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = color;
          e.currentTarget.style.color = "#fff";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "transparent";
          e.currentTarget.style.color = itemColor;
        }}
      >
        {showActiveIndicators && (
          <div
            style={{
              width: 14,
              height: 14,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: isActive ? color : "#f0f0f0",
              flexShrink: 0,
              fontSize: 8,
              fontWeight: 700,
              color: isActive ? "#fff" : "transparent",
            }}
          >
            {seqNum}
          </div>
        )}
        <span style={{ flex: 1 }}>{label}</span>
      </div>
    );
  };

  const visibleFilters = includePeriodInSequence
    ? filtersList
    : filtersList.filter((f) => f !== "period");

  return (
    <ClickAwayListener onClickAway={() => handleSetOpen(false)}>
      <div
        ref={containerRef}
        style={{ position: "relative", display: "inline-block" }}
      >
        {/* Trigger */}
        {customTrigger ? (
          <span onClick={() => handleSetOpen((v) => !v)}>{customTrigger}</span>
        ) : (
          <span
            onClick={() => handleSetOpen((v) => !v)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontSize: 11.5,
              color: "#888",
              cursor: "pointer",
              padding: "1px 8px",
              borderRadius: 4,
              border: "1px solid #ddd",
              background: open ? "#f5f5f5" : "#fafafa",
              userSelect: "none",
              whiteSpace: "nowrap",
              marginRight: 8,
            }}
          >
            <span style={{ fontSize: 14, lineHeight: 1, color }}> + </span>
            {t("results.filter_bar.add")}
          </span>
        )}

        {/* Panel — só renderiza após as coordenadas estarem calculadas */}
        {open && panelStyle && (
          <div
            ref={panelRef}
            className="rfb-slim-scroll rfb-panel-enter"
            style={{
              ...panelStyle,
              zIndex: 1300,
              background: "#fff",
              border: "1px solid #e8e8e8",
              borderRadius: 4,
              boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
              minWidth: 190,
              overflowY: "auto",
              overscrollBehavior: "contain",
              padding: "4px 0",
            }}
          >
            {/* Período avulso (antes das seções) quando em contexto per-PL */}
            {includePeriodInSequence &&
              visibleFilters.includes("period") &&
              renderDimRow("period")}
            {includePeriodInSequence &&
              visibleFilters.includes("period") &&
              sections?.length > 0 && (
                <div
                  style={{
                    height: 0,
                    borderTop: "1px solid #f0f0f0",
                    margin: "3px 0",
                  }}
                />
              )}
            {sections && sections.length > 0
              ? sections.map((section, si) => {
                  const sectionFilters = section.filterKeys.filter(
                    (f) => visibleFilters.includes(f) && f !== "period",
                  );
                  if (sectionFilters.length === 0) return null;
                  return (
                    <div key={section.key}>
                      {si > 0 && (
                        <div
                          style={{
                            height: 0,
                            borderTop: "1px solid #f0f0f0",
                            margin: "3px 0",
                          }}
                        />
                      )}
                      {sectionFilters.map(renderDimRow)}
                    </div>
                  );
                })
              : visibleFilters
                  .filter((f) => !includePeriodInSequence || f !== "period")
                  .map(renderDimRow)}
          </div>
        )}
      </div>
    </ClickAwayListener>
  );
}

DimDropdown.defaultProps = {
  commonFilters: {},
  commonFiltersSequence: [],
  sections: [],
  color: "#888",
  customTrigger: null,
  includePeriodInSequence: false,
  showActiveIndicators: true,
};
DimDropdown.propTypes = {
  filtersList: PropTypes.array.isRequired,
  filtersNames: PropTypes.object.isRequired,
  commonFilters: PropTypes.object,
  commonFiltersSequence: PropTypes.array,
  sections: PropTypes.array,
  language: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
  color: PropTypes.string,
  customTrigger: PropTypes.node,
  onOpenChange: PropTypes.func,
  includePeriodInSequence: PropTypes.bool,
  showActiveIndicators: PropTypes.bool,
};
export { DimDropdown };

// ─── ActiveFilterChip ────────────────────────────────────────────────────────
function ActiveFilterChip({
  filterKey,
  filterLabel,
  mode,
  values,
  filtersGroups,
  language,
  color,
  onEdit,
  onRemove,
}) {
  const { t } = useTranslation();
  // Mesmo blend do FilterTag "gray": #555555 + clientColor @ 50%
  const pillBg = blendColors("#555555", color, 0.5);
  // Null → label de exibição para itens em branco
  const blankLabel = t("results.filter_bar.blank_item_label");
  const displayValues = values.map((v) => (v === null ? blankLabel : v));
  let summary;
  if (mode === "group") {
    const groupKey = values[0];
    summary =
      filtersGroups[filterKey]?.[groupKey]?.[language] ||
      filtersGroups[filterKey]?.[groupKey] ||
      groupKey;
  } else if (mode === "range") {
    summary = displayValues[0] || "";
  } else {
    // > 5 itens OU texto muito longo (> 50 chars) → "X itens"
    // Excepção: 1 item sempre por extenso.
    if (displayValues.length === 1) {
      summary = displayValues[0];
    } else {
      const joined = displayValues.join(", ");
      summary =
        displayValues.length <= 5 && joined.length <= 50
          ? joined
          : t("results.filter_bar.n_items", { count: displayValues.length });
    }
  }

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        marginRight: 9,
        fontSize: 12,
      }}
    >
      <span
        onClick={onEdit}
        style={{
          color: "#999",
          cursor: "pointer",
          fontWeight: 400,
          marginRight: 6,
          textTransform: "lowercase",
        }}
      >
        {filterLabel}
      </span>
      <span
        onClick={onEdit}
        style={{
          display: "inline-flex",
          alignItems: "center",
          background: pillBg,
          color: "#fff",
          padding: "0 8px",
          borderRadius: 20,
          fontSize: 11,
          height: 20,
          cursor: "pointer",
          userSelect: "none",
          gap: 4,
          textTransform: "lowercase",
        }}
      >
        {summary}
        <span
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          style={{
            color: "#fff",
            fontSize: 14,
            lineHeight: 1,
            marginLeft: 2,
            cursor: "pointer",
          }}
        >
          ×
        </span>
      </span>
    </div>
  );
}

ActiveFilterChip.propTypes = {
  filterKey: PropTypes.string.isRequired,
  filterLabel: PropTypes.string.isRequired,
  mode: PropTypes.string.isRequired,
  values: PropTypes.array.isRequired,
  filtersGroups: PropTypes.object.isRequired,
  language: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  onEdit: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

// ─── ResultsFilterBar ────────────────────────────────────────────────────────

/**
 * Barra de filtros unificada para as views de Resultados.
 *
 *   Card > CardBody > flex row > DimDropdown + chips + [FilterBarExtras]
 *
 * Props:
 *   `showPeriodExtras`  – exibe FilterBarExtras (período + reset + persistência) à direita
 *   `periodExtrasProps` – props repassadas ao FilterBarExtras
 *   `sections`          – segmentação do dropdown: [{ key, label, filterKeys[] }]
 *   `sequenced`         – (reservado) não afeta a barra visualmente (ordem é sempre por ativação)
 *   `useLegacyDialog`   – usa FilterDialog antigo em vez de DimFilterDialog
 *   `searchEndpoint`    – endpoint de busca para DimFilterDialog
 *   `clientColor`       – cor primária do cliente
 */
export default function ResultsFilterBar({
  filtersList,
  filtersNames,
  filtersGroups,
  commonFilters,
  commonFiltersMode,
  commonFiltersSequence,
  filterMenuClickHandler,
  removeFilter,
  filterDialogProps,
  dimFilterDialogProps,
  clientColor,
  useLegacyDialog,
  searchEndpoint,
  showPeriodExtras,
  periodExtrasProps,
  sections,
  rangeFilters,
  clearRangeFilter,
  extraChips,
  gridItemProps,
  cardStyle,
  barLabel,
}) {
  const { i18n, t } = useTranslation();
  const color = clientColor || "#4caf50";
  const lang = i18n.language;

  // Filtros ativos em ordem de ativação, excluindo período
  const activeFilterKeys = useMemo(
    () =>
      commonFiltersSequence.filter(
        (f) => f !== "period" && (commonFilters[f] || []).length > 0,
      ),
    [commonFiltersSequence, commonFilters],
  );

  return (
    <>
      <style>{SLIM_SCROLL_CSS}</style>
      <GridItem xs={12} {...(gridItemProps || {})}>
        <Card style={{ marginTop: 18, marginBottom: 8, ...(cardStyle || {}) }}>
          <CardBody style={{ paddingTop: 4, paddingBottom: 4 }}>
            {/* Layout: linha única com flex-wrap.
                Chips ficam junto de "Adicionar"; só quebram quando não há espaço. */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                rowGap: 5,
                columnGap: 4,
                minHeight: 26,
              }}
            >
              {/* Label "Filtros" */}
              <span
                style={{
                  fontSize: 12,
                  color: "#888",
                  fontWeight: 400,
                  marginRight: 4,
                  flexShrink: 0,
                }}
              >
                {barLabel || t("results.filter_bar.label")}
              </span>

              {/* Dropdown "Adicionar" */}
              <DimDropdown
                filtersList={filtersList.current}
                filtersNames={filtersNames.current}
                commonFilters={commonFilters}
                commonFiltersSequence={commonFiltersSequence}
                sections={sections}
                language={lang}
                onSelect={filterMenuClickHandler()}
                color={color}
              />

              {/* Chips inline — quebram naturalmente quando não há espaço */}
              {activeFilterKeys.map((f) => (
                <ActiveFilterChip
                  key={f}
                  filterKey={f}
                  filterLabel={filtersNames.current[f]?.[lang] || f}
                  mode={commonFiltersMode[f] || "items"}
                  values={commonFilters[f] || []}
                  filtersGroups={filtersGroups.current}
                  language={lang}
                  color={color}
                  onEdit={() => filterMenuClickHandler()(f)}
                  onRemove={() => removeFilter(f, null, null, true /* all */)}
                />
              ))}

              {/* Range filter chips (maior/menor/entre) — mesmo componente dos filtros por itens */}
              {rangeFilters &&
                Object.entries(rangeFilters).map(([key, rf]) => (
                  <ActiveFilterChip
                    key={`range-${key}`}
                    filterKey={key}
                    filterLabel={filtersNames.current[key]?.[lang] || key}
                    mode="range"
                    values={[rf.label || ""]}
                    filtersGroups={{}}
                    language={lang}
                    color={color}
                    onEdit={() => filterMenuClickHandler()(key)}
                    onRemove={() => clearRangeFilter && clearRangeFilter(key)}
                  />
                ))}

              {/* extraChips: retrocompatibilidade (deprecated — prefer rangeFilters) */}
              {extraChips}

              {/* Espaçador: empurra FilterBarExtras para a direita */}
              <div style={{ flexGrow: 1 }} />

              {/* Extras de período */}
              {showPeriodExtras && (
                <FilterBarExtras {...periodExtrasProps} clientColor={color} />
              )}
            </div>
          </CardBody>
        </Card>
      </GridItem>

      {/* Modal de filtro */}
      {useLegacyDialog ? (
        <FilterDialog {...filterDialogProps} clientColor={color} />
      ) : (
        <DimFilterDialog
          {...dimFilterDialogProps}
          clientColor={color}
          searchEndpoint={searchEndpoint}
        />
      )}
    </>
  );
}

ResultsFilterBar.defaultProps = {
  useLegacyDialog: false,
  searchEndpoint: null,
  showPeriodExtras: false,
  periodExtrasProps: {},
  sections: [],
  rangeFilters: null,
  clearRangeFilter: null,
  extraChips: null,
  gridItemProps: null,
  cardStyle: null,
  barLabel: null,
};

ResultsFilterBar.propTypes = {
  filtersList: PropTypes.object.isRequired,
  filtersNames: PropTypes.object.isRequired,
  filtersGroups: PropTypes.object.isRequired,
  commonFilters: PropTypes.object.isRequired,
  commonFiltersMode: PropTypes.object.isRequired,
  commonFiltersSequence: PropTypes.array.isRequired,
  filterMenuClickHandler: PropTypes.func.isRequired,
  removeFilter: PropTypes.func.isRequired,
  filterDialogProps: PropTypes.object,
  dimFilterDialogProps: PropTypes.object,
  clientColor: PropTypes.string.isRequired,
  useLegacyDialog: PropTypes.bool,
  searchEndpoint: PropTypes.string,
  showPeriodExtras: PropTypes.bool,
  periodExtrasProps: PropTypes.object,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
      filterKeys: PropTypes.arrayOf(PropTypes.string),
    }),
  ),
  rangeFilters: PropTypes.object,
  clearRangeFilter: PropTypes.func,
  extraChips: PropTypes.node,
  gridItemProps: PropTypes.object,
  cardStyle: PropTypes.object,
  barLabel: PropTypes.string,
};
